import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import {
  calculateReputationScore,
  getUserLevel,
  getNextLevelProgress,
  checkAchievements,
  ACHIEVEMENTS,
  formatReputationScore
} from '../utils/reputationSystem';
import { Trophy, TrendingUp, Award, Star, Shield, Zap } from 'lucide-react';

export default function ReputationCard({ userId }) {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reputationData, setReputationData] = useState(null);

  useEffect(() => {
    if (!userId) return;

    const fetchUserStats = async () => {
      try {
        // Fetch all posts by user
        const postsRef = collection(db, 'posts');
        const postsQuery = query(postsRef, where('authorId', '==', userId));
        const postsSnapshot = await getDocs(postsQuery);

        let totalPosts = postsSnapshot.size;
        let verifiedPosts = 0;
        let totalLikes = 0;
        let totalComments = 0;

        for (const postDoc of postsSnapshot.docs) {
          const postData = postDoc.data();
          if (postData.verified) verifiedPosts++;
          totalLikes += (postData.likes || []).length;

          // Count comments
          const commentsRef = collection(db, 'posts', postDoc.id, 'comments');
          const commentsSnapshot = await getDocs(commentsRef);
          totalComments += commentsSnapshot.size;
        }

        // Fetch verification stats
        const allPostsSnapshot = await getDocs(collection(db, 'posts'));
        let verificationsPerformed = 0;
        let accurateVerifications = 0;

        for (const postDoc of allPostsSnapshot.docs) {
          const postData = postDoc.data();
          const userVerifications = (postData.verifications || []).filter(
            v => v.verifierId === userId
          );

          verificationsPerformed += userVerifications.length;

          // Count accurate verifications (if post is now verified and user approved it)
          if (postData.verified) {
            const userApproved = userVerifications.some(v => v.status === 'approved');
            if (userApproved) accurateVerifications++;
          }
        }

        // Fetch channel messages count
        const channelMessagesRef = collection(db, 'channelMessages');
        const channelMessagesQuery = query(channelMessagesRef, where('authorId', '==', userId));
        const channelMessagesSnapshot = await getDocs(channelMessagesQuery);
        const channelMessages = channelMessagesSnapshot.size;

        // Get user creation date from users collection
        const userDoc = await getDocs(query(collection(db, 'users'), where('uid', '==', userId)));
        let accountAge = 0;
        if (!userDoc.empty) {
          const userData = userDoc.docs[0].data();
          if (userData.createdAt) {
            const createdDate = userData.createdAt.toDate();
            accountAge = Math.floor((Date.now() - createdDate) / (1000 * 60 * 60 * 24));
          }
        }

        const stats = {
          totalPosts,
          verifiedPosts,
          totalLikes,
          totalComments,
          verificationsPerformed,
          accurateVerifications,
          channelMessages,
          accountAge,
          userId
        };

        // Check achievements
        const achievements = checkAchievements(stats);
        stats.achievements = achievements;

        // Calculate reputation
        const reputation = calculateReputationScore(stats);
        const level = getUserLevel(reputation.finalScore);
        const progress = getNextLevelProgress(reputation.finalScore);

        setUserData(stats);
        setReputationData({
          ...reputation,
          level,
          progress,
          achievements
        });
        setLoading(false);
      } catch (error) {
        console.error('Error fetching user stats:', error);
        setLoading(false);
      }
    };

    fetchUserStats();
  }, [userId]);

  if (loading) {
    return (
      <div className="glass-card rounded-xl p-6 border border-purple-500/20">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-700 rounded w-1/2 mb-4"></div>
          <div className="h-8 bg-gray-700 rounded w-3/4"></div>
        </div>
      </div>
    );
  }

  if (!reputationData) {
    return null;
  }

  const { level, progress, finalScore, trustMultiplier, achievements } = reputationData;
  const verificationAccuracy = userData.verificationsPerformed > 0
    ? Math.round((userData.accurateVerifications / userData.verificationsPerformed) * 100)
    : 100;

  return (
    <div className="space-y-4">
      {/* Main Reputation Card */}
      <div className="glass-card rounded-xl p-6 border border-purple-500/20">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <Trophy className="text-purple-400" size={24} />
            Reputation
          </h3>
          <div className="text-right">
            <div className="text-3xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
              {formatReputationScore(finalScore)}
            </div>
            <div className="text-xs text-gray-500">points</div>
          </div>
        </div>

        {/* Level */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="text-2xl">{level.icon}</span>
              <span className={`font-bold ${level.color}`}>{level.name}</span>
            </div>
            {progress.nextLevel && (
              <span className="text-sm text-gray-400">
                {progress.pointsNeeded} to {progress.nextLevel.name}
              </span>
            )}
          </div>

          {/* Progress Bar */}
          {progress.nextLevel && (
            <div className="w-full bg-gray-700 rounded-full h-2">
              <div
                className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full transition-all"
                style={{ width: `${progress.progressPercentage}%` }}
              ></div>
            </div>
          )}
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="glass-card rounded-lg p-3 border border-cyan-500/20">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp size={16} className="text-cyan-400" />
              <span className="text-xs text-gray-400">Contributions</span>
            </div>
            <div className="text-xl font-bold text-white">{userData.totalPosts}</div>
          </div>

          <div className="glass-card rounded-lg p-3 border border-green-500/20">
            <div className="flex items-center gap-2 mb-1">
              <Shield size={16} className="text-green-400" />
              <span className="text-xs text-gray-400">Verified</span>
            </div>
            <div className="text-xl font-bold text-white">{userData.verifiedPosts}</div>
          </div>

          <div className="glass-card rounded-lg p-3 border border-yellow-500/20">
            <div className="flex items-center gap-2 mb-1">
              <Star size={16} className="text-yellow-400" />
              <span className="text-xs text-gray-400">Total Likes</span>
            </div>
            <div className="text-xl font-bold text-white">{userData.totalLikes}</div>
          </div>

          <div className="glass-card rounded-lg p-3 border border-purple-500/20">
            <div className="flex items-center gap-2 mb-1">
              <Zap size={16} className="text-purple-400" />
              <span className="text-xs text-gray-400">Accuracy</span>
            </div>
            <div className="text-xl font-bold text-white">{verificationAccuracy}%</div>
          </div>
        </div>

        {/* Trust Score */}
        <div className="glass-card rounded-lg p-3 border border-blue-500/20">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-400">Trust Multiplier</span>
            <span className={`font-bold ${trustMultiplier >= 1 ? 'text-green-400' : 'text-yellow-400'}`}>
              {trustMultiplier.toFixed(2)}x
            </span>
          </div>
        </div>
      </div>

      {/* Achievements */}
      {achievements.length > 0 && (
        <div className="glass-card rounded-xl p-6 border border-yellow-500/20">
          <h3 className="text-lg font-bold text-white flex items-center gap-2 mb-4">
            <Award className="text-yellow-400" size={24} />
            Achievements ({achievements.length})
          </h3>
          <div className="grid grid-cols-2 gap-3">
            {achievements.map(achId => {
              const achievement = Object.values(ACHIEVEMENTS).find(a => a.id === achId);
              if (!achievement) return null;

              return (
                <div
                  key={achId}
                  className="glass-card rounded-lg p-3 border border-yellow-500/20 hover:border-yellow-500/40 transition-all"
                  title={achievement.description}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xl">{achievement.icon}</span>
                    <span className="text-sm font-semibold text-white">{achievement.name}</span>
                  </div>
                  <p className="text-xs text-gray-400 line-clamp-2">{achievement.description}</p>
                  <div className="text-xs text-yellow-400 mt-1">+{achievement.points} pts</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
